import RawHighcharts from 'highcharts'
export const formatDate = (dateParam: string | Date, options?: { useShortMonth?: boolean }) => {
    const date = dateParam instanceof Date ? dateParam : new Date(dateParam)
    const pad = (num: number) => ('0' + String(num)).slice(-2)
    if (options?.useShortMonth) {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    } else {
        return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear().toString().slice(-2)}`
    }
}

export const getAssignmentStatus = (params: { availableDate?: string | Date; dueDate?: string | Date }) => {
    if (!params.availableDate) {
        return 'draft'
    }
    const startDate = params.availableDate instanceof Date ? params.availableDate : new Date(params.availableDate)
    const dueDate = params.dueDate
        ? params.dueDate instanceof Date ? params.dueDate : new Date(params.dueDate)
        : undefined
    const today = new Date()

    if (dueDate && today.getTime() > dueDate.getTime()) {
        return 'closed'
    } else if (today.getTime() > startDate.getTime()) {
        return 'available'
    } else {
        return 'upcoming'
    }
}

export const daysBetween = (
    params: {
        startDate: Date | string
        endDate: Date | string
    }): number => {
    const startDate = params.startDate instanceof Date ? params.startDate : new Date(params.startDate)
    const endDate = params.endDate instanceof Date ? params.endDate : new Date(params.endDate)

    // Normalize both dates to midnight for full-day comparison
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())

    const msPerDay = 1000 * 60 * 60 * 24
    const diffInMs = end.getTime() - start.getTime()

    return Math.floor(diffInMs / msPerDay)
}

export type TActivityMode = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 10 | 11
export const getActivityInfo = (mode: TActivityMode) => {
    const activityMap = {
        '-1': {
            name: 'Question of the Day',
            icon: 'calendar',
        },
        '0': {
            name: 'Quick 10',
            icon: 'quick10',
        },
        '1': {
            name: 'Legacy',
            icon: 'pencil',
        },
        '2': {
            name: 'Missed Questions Quiz',
            icon: 'missedQuestions',
        },
        '3': {
            name: 'Weakest Subjects Quiz',
            icon: 'subject',
        },
        '4': {
            name: 'Timed Quiz',
            icon: 'stopwatch',
        },
        '5': {
            name: 'Mock Exam',
            icon: 'exam',
        },
        '6': {
            name: 'Level Up',
            icon: 'levelup',
        },
        '7': { // capstone recall shows as level up
            name: 'Level Up',
            icon: 'levelup',
        },
        '10': {
            name: 'Custom Quiz',
            icon: 'pencil',
        },
        '11': {
            name: 'Assignment',
            icon: 'link',
        },
    }

    return activityMap[mode] || {
        name: 'Unknown',
        icon: 'pencil',
    }
}

export const getColorFromScore = (score: number) => {
    return score > 74
        ? 'green'
        : score > 50
            ? 'yellow'
            : 'red'
}

export const getHoursMinutesSeconds = (durationSeconds: number) => {
    const hourSeconds = durationSeconds || 0
    const hours = Math.floor(hourSeconds / 3600)
    const minuteSeconds = hourSeconds % 3600
    const minutes = Math.floor(minuteSeconds / 60)
    const seconds = Math.floor(minuteSeconds % 60)

    return {
        hours,
        minutes,
        seconds,
    }
}

export const getDaysAgo = (dateParam: string | Date) => {
    const date = dateParam instanceof Date ? dateParam : new Date(dateParam)
    const now = new Date()

    // Strip time from both dates to compare full calendar days
    const utc1 = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
    const utc2 = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())

    const msPerDay = 1000 * 60 * 60 * 24
    return Math.floor((utc1 - utc2) / msPerDay)
}

export const monthNames = [ 'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December' ]

export const monthsAbrev = [ 'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec' ]

type TCustomSeries = Highcharts.Series & {
    options: {
        borderRadiusTopLeft: number
        borderRadiusTopRight: number
        borderRadiusBottomRight: number
        borderRadiusBottomLeft: number
    }
}

type TShapeArgs = {
    d?: Highcharts.SVGPathArray
    width?: number
    height?: number
    x?: number
    y?: number
}

type TCustomPoint = Highcharts.Point & {
    shapeArgs: TShapeArgs
    shapeType: string
    dlBox: TShapeArgs
}

type TCustomHighcharts = typeof RawHighcharts & {
    seriesTypes: {
        column: {
            prototype: Record<string, unknown>
        }
    }
}

const addRoundedCorners = (H: TCustomHighcharts): TCustomHighcharts => {
    H.wrap((H.seriesTypes.column.prototype), 'translate', function (this: TCustomSeries, proceed: () => void) {
        const options = this.options

        proceed.call(this)

        const points = this.points as TCustomPoint[]
        points.forEach(point => {
            if (options.borderRadiusTopLeft || options.borderRadiusTopRight) {
                const w = Number(point.shapeArgs.width)
                const h = Number(point.shapeArgs.height)
                const x = Number(point.shapeArgs.x)
                const y = Number(point.shapeArgs.y)
                const maxR = Math.min(w, h) / 2

                let radiusTopLeft = H.relativeLength(options.borderRadiusTopLeft || 0, w)
                let radiusTopRight = H.relativeLength(options.borderRadiusTopRight || 0, w)


                radiusTopLeft = radiusTopLeft > maxR ? maxR : radiusTopLeft
                radiusTopRight = radiusTopRight > maxR ? maxR : radiusTopRight

                point.dlBox = point.shapeArgs

                point.shapeType = 'path'
                const d = [
                    'M',
                    x + radiusTopLeft,
                    y,
                    'L',
                    x + w - radiusTopRight,
                    y,
                    'C',
                    x + w - (radiusTopRight / 2),
                    y,
                    x + w,
                    y + (radiusTopRight / 2),
                    x + w,
                    y + radiusTopRight,
                    'L',
                    x + w,
                    y + h,
                    'C',
                    x + w,
                    y + h,
                    x + w,
                    y + h,
                    x + w,
                    y + h,
                    'L',
                    x,
                    y + h,
                    'C',
                    x,
                    y + h,
                    x,
                    y + h,
                    x,
                    y + h,
                    'L',
                    x,
                    y + radiusTopLeft,
                    'C',
                    x,
                    y + (radiusTopLeft / 2),
                    x + (radiusTopLeft / 2),
                    y,
                    x + radiusTopLeft,
                    y,
                    'Z',
                ] as unknown as Highcharts.SVGPathArray
                point.shapeArgs = {
                    d,
                }
            }

        })
    })
    return H
}

export const Highcharts = addRoundedCorners(RawHighcharts as TCustomHighcharts)

export const getCookie = (cookieName: string) => {
    const cookieLib = document.cookie.split('; ').reduce((acc, cookie) => {
        const [ name, value ] = cookie.split('=')
        if (name !== undefined) {
            acc[name] = value || ''
        }
        return acc
    }, {} as Record<string, string>)
    return cookieLib[cookieName]
}

export const setCookie = ({ name, value }: {
    name: string
    value: string
}) => {
    const cookieDomain = import.meta.env.VUE_APP_COOKIE_DOMAIN
    if (cookieDomain) {
        document.cookie = `${name}=${value}; domain=${cookieDomain}`
    } else {
        document.cookie = `${name}=${value}`
    }
}

export const clearCookie = (cookieName: string) => {
    const cookieDomain = import.meta.env.VUE_APP_COOKIE_DOMAIN
    if (cookieDomain) {
        document.cookie = `${cookieName}=; domain=${cookieDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`
    } else {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`
    }
}

export const environment = (() => {
    if (import.meta.env.VUE_APP_PARSE_URL) {
        if (import.meta.env.VUE_APP_PARSE_URL.includes('localhost')) {
            return 'localhost'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('test')) {
            return 'test'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('development')) {
            return 'development'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('staging')) {
            return 'staging'
        }
    }
    return 'production'
})()

export const isSupportWindow = () => {
    return window.location.origin.includes('support')
}

// TypeScript uses 'instanceof Promise' to narrow Promises, but not all PromiseLike types
// This is a helper function to also narrow PromiseLike types in our context
export const isPromise = (val: unknown): val is PromiseLike<unknown> => {
    return val instanceof Promise
}

export const buildUTMParamString = ({ utm_campaign, utm_content, utm_term }: {
    utm_campaign: string
    utm_content: string
    utm_term?: string
}) => {
    const urlSearchParams = new URLSearchParams({
        utm_source: 'web',
        utm_medium: 'teach_app',
        utm_campaign,
        utm_content,
    })
    if (utm_term) {
        urlSearchParams.append('utm_term', utm_term)
    }
    return urlSearchParams.toString()
}

export const compareSemanticVersionsDescending = (a: string, b: string) => {
    return -a.localeCompare(b, undefined, { numeric: true })
}

export const stripHtmlTags = (string?: string) => {
    if (string) {
        const div = document.createElement('div')
        div.innerHTML = string
        return div.textContent || ''
    }
    return ''
}
